import { useState } from "react";
import { Mail, Send, Loader2, MessageSquare, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { Reveal } from "../components/Reveal";
import Seo from "../components/Seo";
import { api, formatApiError } from "../lib/api";

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await api.post("/contact", form);
      setDone(true);
      toast.success("Message sent! We'll reply within 24 hours.");
      setForm({ name: "", email: "", subject: "", message: "" });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSending(false);
    }
  };

  return (
    <div data-testid="contact-page" className="pt-32 pb-24 min-h-screen relative">
      <Seo title="Support — ASCENDXP" description="Contact ASCENDXP support. We're here to help with billing, accounts, and more." />
      <div className="absolute top-10 right-0 w-[500px] h-[400px] rounded-full bg-indigo-600/10 blur-[120px]" />
      <div className="max-w-5xl mx-auto px-6 lg:px-8 relative grid lg:grid-cols-2 gap-12">
        <Reveal>
          <p className="text-xs uppercase tracking-[0.3em] text-indigo-400 font-bold mb-4">Support</p>
          <h1 className="font-heading font-black text-4xl sm:text-5xl tracking-tighter">Get in touch</h1>
          <p className="mt-5 text-zinc-400 leading-relaxed">
            Questions about billing, your account, or the arena? Our team responds within 24 hours.
          </p>
          <div className="mt-8 space-y-4">
            <a href="mailto:support@ascendind.in" className="flex items-center gap-4 glass rounded-2xl p-5 hover:bg-white/[0.06] transition-colors">
              <div className="w-11 h-11 rounded-xl bg-indigo-600/15 text-indigo-400 grid place-items-center"><Mail className="w-5 h-5" /></div>
              <div>
                <div className="text-xs text-zinc-500">Email us</div>
                <div className="font-semibold">support@ascendind.in</div>
              </div>
            </a>
            <div className="flex items-center gap-4 glass rounded-2xl p-5">
              <div className="w-11 h-11 rounded-xl bg-[#d4af37]/15 text-[#d4af37] grid place-items-center"><MessageSquare className="w-5 h-5" /></div>
              <div>
                <div className="text-xs text-zinc-500">Response time</div>
                <div className="font-semibold">Within 24 hours</div>
              </div>
            </div>
          </div>
        </Reveal>

        <Reveal delay={0.1}>
          <form data-testid="contact-form" onSubmit={submit} className="glass rounded-2xl p-8">
            {done ? (
              <div data-testid="contact-success" className="text-center py-10">
                <CheckCircle2 className="w-14 h-14 text-emerald-400 mx-auto" />
                <h3 className="font-heading font-bold text-xl mt-4">Message sent</h3>
                <p className="text-zinc-400 text-sm mt-2">We'll be in touch soon.</p>
                <button type="button" onClick={() => setDone(false)} className="btn-ghost mt-6">Send another</button>
              </div>
            ) : (
              <div className="space-y-4">
                <input data-testid="contact-name" required placeholder="Your name" className="input-dark"
                  value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
                <input data-testid="contact-email" required type="email" placeholder="Email address" className="input-dark"
                  value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
                <input data-testid="contact-subject" required placeholder="Subject" className="input-dark"
                  value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} />
                <textarea data-testid="contact-message" required rows={5} placeholder="How can we help?" className="input-dark resize-none"
                  value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} />
                <button data-testid="contact-submit" disabled={sending} className="btn-primary w-full disabled:opacity-60">
                  {sending ? <><Loader2 className="w-5 h-5 animate-spin" /> Sending…</> : <><Send className="w-5 h-5" /> Send Message</>}
                </button>
              </div>
            )}
          </form>
        </Reveal>
      </div>
    </div>
  );
}
